import { NoteType, type Note } from '../note'

/**
 * Helper function to check if a note is in trash by checking if it's a descendant of @Trash folder
 */
function isInTrash(note: Note, notes: Map<string, Note>): boolean {
  let current: Note | undefined = note
  while (current) {
    if (current.filename === '@Trash' && current.isFolder) {
      return true
    }
    if (!current.parent) break
    current = notes.get(current.parent)
  }
  return false
}

/**
 * Creates an "Imported Notes" folder with auto-incrementing suffix if needed
 *
 * @param createNoteFunction - Function to create a note/folder
 * @param projectNotesMap - Map of existing project notes
 * @returns Object containing the folder name and created folder note
 */
export async function createImportedNotesFolder(
  createNoteFunction: (parameters: {
    recordName: string
    noteType: NoteType
    filename: string
    content: string
    parent: string | null
    isDir?: boolean
  }) => Promise<Note>,
  projectNotesMap: Map<string, Note>
): Promise<{ folderName: string; folder: Note }> {
  const IMPORTED_NOTES_BASE = 'Imported Notes'

  // Helper function to check if a root-level folder exists with the given name
  // Only checks root-level folders (parent == null) to avoid conflicts with
  // folders in trash or nested folders with the same name
  // Also excludes folders that are in the trash
  const findRootFolder = (name: string): Note | undefined => {
    return [...projectNotesMap.values()].find(
      (note) =>
        note.filename === name &&
        note.noteType === NoteType.PROJECT_NOTE &&
        // eslint-disable-next-line unicorn/no-null -- Check for root level folders
        note.parent == null &&
        !isInTrash(note, projectNotesMap)
    )
  }

  // Find available folder name by iterating numbers
  let folderName = IMPORTED_NOTES_BASE
  let folderNumber = 1
  let existingFolder = findRootFolder(folderName)

  while (existingFolder) {
    folderNumber++
    folderName = `${IMPORTED_NOTES_BASE} ${String(folderNumber)}`
    existingFolder = findRootFolder(folderName)
  }

  // Create the new folder
  const folder = await createNoteFunction({
    recordName: crypto.randomUUID(),
    noteType: NoteType.PROJECT_NOTE,
    filename: folderName,
    content: '',
    parent: null,
    isDir: true,
  })

  return { folderName, folder }
}
